import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Modal from './Modal';
import CommentThread from './CommentThread';
import { useToast } from './Toast';
import { useAuth } from '../AuthContext';
import { db } from '../firebaseConfig';
import { useEntityList, deleteEntity, updateEntity } from '../data/entities';
import {
  CATEGORY_LABELS,
  DP_STATUS_LABELS,
  DP_DECISIONS,
  DP_DECISION_LABELS,
  DP_DECISION_TONE,
  DP_DECISION_AUDIT_TYPES,
  decisionOf,
  undoDecision,
  POSITION_STATUS_LABELS,
  VISIBILITY_LABELS,
  VISIBILITY_TONE,
  PARTY_COLORS,
  createDealPoint,
  setPosition,
  setDealPointStatus,
  setDealPointLock,
  acceptSuggestedPoint,
  respondToProposal,
  applyLanguageToDraft,
  loadVersionClauses,
  actorOf,
} from '../data/deals';
import { aiCompromise, isAiConfigured, downloadWordExport, aiCorrespondence, aiConformance, aiGenerateMemo } from '../data/legalApi';

const STATUS_TONE = { open: 'b-info', agreed: 'b-ok', fallenAway: '', escalated: 'b-danger' };
const POS_TONE = { open: 'b-info', agreed: 'b-ok', rejected: 'b-danger', countered: 'b-warn' };
const PRIORITY_MARK = { low: '·', medium: '••', high: '•••', critical: '!' };

// Live view of each linked document's CURRENT draft clauses, so every deal
// point can show the language actually on the table right now.
function useCurrentClauses(scope, points) {
  const docIds = useMemo(() => {
    const ids = new Set();
    for (const p of points || []) for (const r of p.clauseRefs || []) if (r.docId) ids.add(r.docId);
    return [...ids].sort();
  }, [points]);

  const [map, setMap] = useState({}); // docId -> { title, versionNumber, clauses: { clauseId: clause } }

  useEffect(() => {
    if (!scope || !docIds.length) return undefined;
    const unsubs = docIds.map((docId) =>
      db.doc(`orgs/${scope.org}/deals/${scope.deal}/docs/${docId}`).onSnapshot(async (docSnap) => {
        if (!docSnap.exists) return;
        const d = docSnap.data();
        if (!d.currentVersionId) return;
        const vSnap = await db
          .doc(`orgs/${scope.org}/deals/${scope.deal}/docs/${docId}/versions/${d.currentVersionId}`)
          .get();
        if (!vSnap.exists) return;
        const v = vSnap.data();
        // Large versions spill clause arrays to S3 — hydrate before mapping.
        const clauseArr = await loadVersionClauses(v).catch(() => v.clauses || []);
        setMap((m) => ({
          ...m,
          [docId]: {
            title: d.title,
            versionNumber: v.number,
            clauses: Object.fromEntries(clauseArr.map((c) => [c.id, c])),
          },
        }));
      })
    );
    return () => unsubs.forEach((u) => u());
  }, [scope, docIds]);

  return map;
}

function PositionModal({ scope, participant, point, party, preset, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const existing = (point.positions || {})[party.key] || {};
  const [stance, setStance] = useState(preset?.stance ?? existing.stance ?? '');
  const [rationale, setRationale] = useState(preset?.rationale ?? existing.rationale ?? '');
  const [status, setStatus] = useState(preset?.status ?? existing.status ?? 'open');
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await setPosition(scope, actorOf(user, participant), point, party.key, { stance, rationale, status });
      toast('Position recorded', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`${party.label} — ${point.title}`} onClose={onClose} maxWidth={640}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={save}>
        <div className="cs-field">
          <label htmlFor="pos-status">Status</label>
          <select id="pos-status" className="cs-input" value={status} onChange={(e) => setStatus(e.target.value)}>
            {Object.entries(POSITION_STATUS_LABELS).map(([k, v]) => (
              <option key={k} value={k}>{v}</option>
            ))}
          </select>
        </div>
        <div className="cs-field">
          <label htmlFor="pos-stance">Proposed language / position</label>
          <textarea
            id="pos-stance"
            className="cs-input"
            rows={6}
            value={stance}
            onChange={(e) => setStance(e.target.value)}
            placeholder="The exact language (or commercial position) this party proposes"
          />
        </div>
        <div className="cs-field">
          <label htmlFor="pos-rationale">Rationale (visible to all parties)</label>
          <textarea
            id="pos-rationale"
            className="cs-input"
            rows={2}
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
          />
        </div>
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy}>Save position</button>
        </div>
      </form>
    </Modal>
  );
}

function PointDetailModal({ scope, participant, point, parties, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const { docs: events } = useEntityList('dpEvent', { ...scope, dealPoint: point.id });
  const [busy, setBusy] = useState(false);

  const actor = actorOf(user, participant);

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      await setDealPointStatus(scope, actor, point, status);
      toast(`Status → ${DP_STATUS_LABELS[status]}`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const toggleLock = async () => {
    setBusy(true);
    try {
      await setDealPointLock(scope, actor, point, !point.locked);
      toast(point.locked ? 'Unlocked' : 'Locked — linked language frozen', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  const markReviewed = async () => {
    setBusy(true);
    try {
      await updateEntity('dealPoint', scope, point.id, { needsReview: false, needsReviewNote: '' });
      toast('Marked reviewed', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={point.title} onClose={onClose} maxWidth={760}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          <span className={`cs-badge ${STATUS_TONE[point.status] || ''}`}>{DP_STATUS_LABELS[point.status]}</span>
          <span className="cs-chip">{CATEGORY_LABELS[point.category] || point.category}</span>
          <span className="cs-chip">priority: {point.priority}</span>
          {point.locked && <span className="cs-badge b-warn">locked</span>}
          {point.needsReview && <span className="cs-badge b-danger">needs review</span>}
          {point.visibility && (
            <span className={`cs-badge ${VISIBILITY_TONE[point.visibility] || ''}`}>
              {VISIBILITY_LABELS[point.visibility] || point.visibility}
            </span>
          )}
        </div>

        {point.needsReview && (
          <div className="cs-banner b-warn u-small">
            A received draft changed this point's linked language{point.needsReviewNote ? `: ${point.needsReviewNote}` : '.'}
            <button className="cs-btn cs-btn-outline cs-btn-sm" style={{ marginLeft: 'var(--sp-3)' }} disabled={busy} onClick={markReviewed}>
              Mark reviewed
            </button>
          </div>
        )}

        {point.detail && <p style={{ margin: 0 }}>{point.detail}</p>}

        {(point.changeLog || []).length > 0 && (
          <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
            <strong className="u-small">Authorship / dates</strong>
            {point.changeLog.map((row, i) => (
              <div key={i} className="u-small">
                <span className="u-tabular u-muted">{row.dateLabel || ''}</span>
                {' · '}
                <strong>{row.author}</strong>
                {row.party ? <span className="u-muted"> ({row.party})</span> : null}
                {row.action ? <span className="u-muted"> — {row.action}</span> : null}
                {row.note ? <div className="u-muted">{row.note}</div> : null}
              </div>
            ))}
          </div>
        )}

        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {point.status !== 'escalated' && (
            <button className="cs-btn cs-btn-outline cs-btn-sm" disabled={busy} onClick={() => changeStatus('escalated')}>
              Escalate
            </button>
          )}
          {point.status !== 'fallenAway' && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={() => changeStatus('fallenAway')}>
              Fallen away
            </button>
          )}
          {point.status !== 'open' && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={() => changeStatus('open')}>
              Reopen
            </button>
          )}
          <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={busy} onClick={toggleLock}>
            {point.locked ? 'Unlock' : 'Lock'}
          </button>
        </div>

        <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <strong className="u-small">History</strong>
          {events === null && <div className="cs-skeleton" style={{ height: 40 }} />}
          {events && !events.length && <span className="u-small u-muted">No events yet.</span>}
          {(events || []).map((ev) => (
            <div key={ev.id} className="u-small u-muted">
              <span className="u-tabular">{new Date(ev.createdAt).toLocaleString()}</span> — {ev.byName}
              {ev.partyKey ? ` (${ev.partyKey})` : ''}: {ev.summary}
            </div>
          ))}
        </div>

        <CommentThread
          scope={scope}
          participant={participant}
          anchor={{ kind: 'dealPoint', dealPointId: point.id }}
        />
      </div>
    </Modal>
  );
}

function NewPointModal({ scope, participant, onClose }) {
  const { user } = useAuth();
  const toast = useToast();
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');
  const [category, setCategory] = useState('commercial');
  const [priority, setPriority] = useState('medium');
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return;
    setBusy(true);
    try {
      await createDealPoint(scope, actorOf(user, participant), {
        title: title.trim(),
        detail: detail.trim(),
        category,
        priority,
        status: 'open',
        source: 'human',
        suggested: false,
      });
      toast('Deal point added', 'ok');
      onClose();
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New deal point" onClose={onClose}>
      <form className="u-stack" style={{ '--gap': 'var(--sp-4)' }} onSubmit={submit}>
        <div className="cs-field">
          <label htmlFor="dp-title">Issue</label>
          <input id="dp-title" className="cs-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Purchase Price Adjustment — Working Capital Collar" autoFocus />
        </div>
        <div className="cs-field">
          <label htmlFor="dp-detail">Detail</label>
          <textarea id="dp-detail" className="cs-input" rows={3} value={detail} onChange={(e) => setDetail(e.target.value)} />
        </div>
        <div className="u-cluster" style={{ '--gap': 'var(--sp-3)' }}>
          <div className="cs-field">
            <label htmlFor="dp-cat">Category</label>
            <select id="dp-cat" className="cs-input" value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="cs-field">
            <label htmlFor="dp-pri">Priority</label>
            <select id="dp-pri" className="cs-input" value={priority} onChange={(e) => setPriority(e.target.value)}>
              {['low', 'medium', 'high', 'critical'].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
        </div>
        <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
          <button type="button" className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="cs-btn cs-btn-primary" disabled={busy || !title.trim()}>Add point</button>
        </div>
      </form>
    </Modal>
  );
}

// Paste a thread / counterparty letter → the deal desk reconciles it against
// the register: matched points get flagged for review (never silently moved),
// new points file as suggestions. The report stays on screen for triage.
function CorrespondenceModal({ scope, onClose }) {
  const toast = useToast();
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    setBusy(true);
    try {
      const r = await aiCorrespondence({ orgId: scope.org, dealId: scope.deal, text });
      setReport(r);
      toast(`Reconciled — ${r.updates.length} point${r.updates.length === 1 ? '' : 's'} flagged, ${r.created.length} suggested new`, 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="File correspondence" onClose={onClose} maxWidth={720}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-4)' }}>
        {!report && (
          <>
            <p className="u-small u-muted" style={{ margin: 0 }}>
              Paste an email thread, letter, or a counterparty's open-items list. It's reconciled against the
              register: points it moves get flagged for your review (statuses are never changed for you), and
              genuinely new issues appear as suggestions to accept or dismiss.
            </p>
            <textarea
              className="cs-input"
              rows={12}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Paste the correspondence here…"
              autoFocus
            />
            <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
              <button className="cs-btn cs-btn-ghost" onClick={onClose}>Cancel</button>
              <button className="cs-btn cs-btn-primary" disabled={busy || !text.trim()} onClick={run}>
                {busy ? 'Reconciling…' : 'Reconcile against register'}
              </button>
            </div>
          </>
        )}
        {report && (
          <>
            {report.summary && <p className="u-small" style={{ margin: 0 }}>{report.summary}</p>}
            {report.updates.length > 0 && (
              <div className="u-stack" style={{ '--gap': 'var(--sp-2)' }}>
                <strong className="u-small">Points flagged for review</strong>
                {report.updates.map((u) => (
                  <div key={u.pointId} className="cs-banner b-info u-small" style={{ margin: 0 }}>
                    <strong>{u.title}</strong>
                    {u.suggestedStatus && u.suggestedStatus !== u.currentStatus && (
                      <span className="cs-chip b-warn" style={{ marginLeft: 8 }}>suggests {DP_STATUS_LABELS[u.suggestedStatus] || u.suggestedStatus}</span>
                    )}
                    <div>{u.development}</div>
                    {u.stance && <div className="u-muted">{u.party ? `${u.party}: ` : ''}{u.stance}</div>}
                  </div>
                ))}
              </div>
            )}
            {report.created.length > 0 && (
              <div className="u-small">
                <strong>Suggested new points</strong> (accept or dismiss in the strip above):{' '}
                {report.created.map((c) => c.title).join('; ')}
              </div>
            )}
            {!report.updates.length && !report.created.length && (
              <div className="cs-empty">Nothing in that correspondence moved the register.</div>
            )}
            <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
              <button className="cs-btn cs-btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// Register-vs-draft conformance: every point verified against the current
// draft with per-point evidence. Read-only — the report IS the deliverable.
const VERDICT_TONE = { reflected: 'b-ok', partial: 'b-warn', missing: 'b-danger', 'not-applicable': '' };
function ConformanceModal({ scope, onClose }) {
  const toast = useToast();
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    let gone = false;
    aiConformance({ orgId: scope.org, dealId: scope.deal })
      .then((r) => { if (!gone) { setReport(r); setBusy(false); } })
      .catch((err) => { if (!gone) { toast(err.message, 'danger'); setBusy(false); } });
    return () => { gone = true; };
  }, [scope.org, scope.deal, toast]);

  const counts = useMemo(() => {
    const c = { reflected: 0, partial: 0, missing: 0, 'not-applicable': 0 };
    for (const f of report?.findings || []) c[f.verdict] += 1;
    return c;
  }, [report]);

  return (
    <Modal title="Verify draft against register" onClose={onClose} maxWidth={760}>
      <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
        {busy && <div className="cs-skeleton" style={{ height: 160 }} />}
        {!busy && report && (
          <>
            <p className="u-small u-muted" style={{ margin: 0 }}>
              "{report.docTitle}" v{report.versionNumber} · {counts.reflected} reflected · {counts.partial} partial ·{' '}
              {counts.missing} missing{counts['not-applicable'] ? ` · ${counts['not-applicable']} n/a` : ''}
            </p>
            {report.summary && <p className="u-small" style={{ margin: 0 }}>{report.summary}</p>}
            <div className="u-stack" style={{ '--gap': 'var(--sp-2)', maxHeight: 420, overflow: 'auto' }}>
              {[...report.findings]
                .sort((a, b) => ['missing', 'partial', 'reflected', 'not-applicable'].indexOf(a.verdict) - ['missing', 'partial', 'reflected', 'not-applicable'].indexOf(b.verdict))
                .map((f) => (
                  <div key={f.pointId} className={`cs-banner u-small ${VERDICT_TONE[f.verdict]}`} style={{ margin: 0 }}>
                    <span className="cs-chip" style={{ marginRight: 8 }}>{f.verdict}</span>
                    <strong>{f.title}</strong>
                    <div>{f.evidence}{f.clauseHeading ? ` (§ ${f.clauseHeading})` : ''}</div>
                  </div>
                ))}
            </div>
            <div className="u-cluster" style={{ justifyContent: 'flex-end' }}>
              <button className="cs-btn cs-btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
        {!busy && !report && <div className="cs-empty">Verification failed — try again.</div>}
      </div>
    </Modal>
  );
}

// One deal point in the negotiation view: current draft language on top,
// then a column per party (their proposed language + accept/reject/counter).
function NegotiationCard({ scope, dealId, point, parties, partyColors, participant, clauseMap, dealPoints, onDetail, onEditPosition }) {
  const { user } = useAuth();
  const toast = useToast();
  const [busy, setBusy] = useState('');
  const actor = actorOf(user, participant);
  const myParty = participant?.partyKey || '';

  const ref = (point.clauseRefs || []).find((r) => clauseMap[r.docId]?.clauses?.[r.clauseId]) || (point.clauseRefs || [])[0];
  const clause = ref ? clauseMap[ref.docId]?.clauses?.[ref.clauseId] : null;
  const docInfo = ref ? clauseMap[ref.docId] : null;

  const respond = async (fromPartyKey, accept) => {
    setBusy(`${accept ? 'accept' : 'reject'}:${fromPartyKey}`);
    const prev = { status: point.status || 'open', positions: point.positions || {} };
    try {
      await respondToProposal(scope, actor, point, parties, fromPartyKey, myParty, accept);
      // The point stays where it is; the chip filter decides what's visible.
      // Undo restores the pre-decision snapshot as a logged edit.
      toast(
        accept ? `"${point.title}" moved to Accepted` : `"${point.title}" moved to Rejected`,
        accept ? 'ok' : 'warn',
        8000,
        { label: 'Undo', onClick: () => undoDecision(scope, actor, point, prev).catch((err) => toast(err.message, 'danger')) }
      );
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  const suggest = async () => {
    setBusy('ai');
    try {
      await aiCompromise({ orgId: scope.org, dealId: scope.deal, dealPointId: point.id });
      toast('AI compromise suggested below', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  // Agreed language = any party's stance whose status is agreed (they match
  // once everyone has accepted).
  const agreedStance = parties.map((p) => (point.positions || {})[p.key]).find((pos) => pos?.status === 'agreed')?.stance || '';
  const canApply = point.status === 'agreed' && ref && clause && agreedStance && agreedStance !== clause.text;

  const apply = async () => {
    if (!window.confirm(`Publish a new version of "${docInfo?.title}" with the agreed language in "${clause.heading}"?`)) return;
    setBusy('apply');
    try {
      await applyLanguageToDraft(scope, actor, ref, agreedStance, point, dealPoints);
      toast('Agreed language applied — new draft version published', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="cs-card u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-split">
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', alignItems: 'baseline' }}>
          <strong>{point.title}</strong>
          <span className={`cs-badge ${DP_DECISION_TONE[decisionOf(point)] || ''}`}>{DP_DECISION_LABELS[decisionOf(point)]}</span>
          {point.locked && <span className="cs-badge b-warn">locked</span>}
          {point.needsReview && (
            <span className="cs-badge b-danger" data-tip={point.needsReviewNote || 'A received draft changed the linked language'}>
              needs review
            </span>
          )}
          {point.visibility && point.visibility !== 'internal' && (
            <span className={`cs-badge ${VISIBILITY_TONE[point.visibility] || ''}`} data-tip={point.newRequest ? 'Do not treat as already on the table — this would be a new ask' : ''}>
              {VISIBILITY_LABELS[point.visibility] || point.visibility}
            </span>
          )}
          <span aria-hidden="true" className="u-xs u-muted" data-tip={`priority: ${point.priority}`}>{PRIORITY_MARK[point.priority] || ''}</span>
        </span>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
          {isAiConfigured() && point.status !== 'agreed' && (
            <button className="cs-btn cs-btn-ghost cs-btn-sm" disabled={!!busy} onClick={suggest}>
              {busy === 'ai' ? 'Thinking…' : 'AI compromise'}
            </button>
          )}
          <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={onDetail}>Details & history</button>
        </span>
      </div>

      {point.detail && <div className="u-small u-muted">{point.detail}</div>}

      {/* Current draft language */}
      {clause && (
        <div className="cs-lang u-stack" style={{ '--gap': 'var(--sp-1)' }}>
          <div className="u-split">
            <span className="u-xs u-muted">
              CURRENT DRAFT LANGUAGE — {clause.heading}{docInfo ? ` · ${docInfo.title} v${docInfo.versionNumber}` : ''}
            </span>
            <Link className="u-xs" to={`/deals/${dealId}/docs/${ref.docId}?clause=${ref.clauseId}`}>
              View in document →
            </Link>
          </div>
          <div className="u-small u-clamp" style={{ '--lines': 4, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
            {clause.text}
          </div>
        </div>
      )}
      {!clause && (point.clauseRefs || []).length > 0 && (
        <div className="u-xs u-muted">Linked: {(point.clauseRefs || []).map((r) => r.heading).join(' · ')}</div>
      )}

      {/* Party columns */}
      <div className="cs-nego-grid">
        {parties.map((party) => {
          const pos = (point.positions || {})[party.key];
          const mine = party.key === myParty;
          const canRespond = myParty && !mine && pos && pos.stance && point.status !== 'agreed' && !point.locked;
          return (
            <div key={party.key} className="cs-nego-col" style={{ '--party-color': partyColors[party.key] }}>
              <div className="u-split">
                <span className="u-cluster" style={{ '--gap': '6px', alignItems: 'center' }}>
                  <span className="cs-sidebar-dot" style={{ background: partyColors[party.key] }} />
                  <strong className="u-small">{party.label}</strong>
                  {mine && <span className="u-xs u-muted">(you)</span>}
                </span>
                {pos && <span className={`cs-badge ${POS_TONE[pos.status] || ''}`}>{POSITION_STATUS_LABELS[pos.status] || pos.status}</span>}
              </div>
              {pos?.stance ? (
                <div className="u-small" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.55 }}>{pos.stance}</div>
              ) : (
                <div className="u-small u-muted">No position recorded</div>
              )}
              {pos?.rationale && <div className="u-xs u-muted">{pos.rationale}</div>}
              {pos?.at && <div className="u-xs u-muted u-tabular">{pos.byName} · {new Date(pos.at).toLocaleDateString()}</div>}
              <div className="u-cluster" style={{ '--gap': 'var(--sp-2)', marginTop: 'auto' }}>
                {canRespond && (
                  <>
                    <button
                      className="cs-btn cs-btn-primary cs-btn-sm"
                      disabled={!!busy}
                      onClick={() => respond(party.key, true)}
                    >
                      {busy === `accept:${party.key}` ? 'Accepting…' : 'Accept'}
                    </button>
                    <button
                      className="cs-btn cs-btn-outline cs-btn-sm"
                      disabled={!!busy}
                      onClick={() => respond(party.key, false)}
                    >
                      {busy === `reject:${party.key}` ? '…' : 'Reject'}
                    </button>
                  </>
                )}
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => onEditPosition(party)}>
                  {pos ? 'Edit' : mine ? 'Propose language' : '+ position'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI compromise + counter path */}
      {point.aiSuggestion?.text && point.status !== 'agreed' && (
        <div className="cs-banner b-info u-stack" style={{ '--gap': 'var(--sp-2)' }}>
          <strong className="u-small">AI-suggested compromise</strong>
          <div className="u-small" style={{ whiteSpace: 'pre-wrap' }}>{point.aiSuggestion.text}</div>
          {myParty && (
            <div className="u-cluster">
              <button
                className="cs-btn cs-btn-outline cs-btn-sm"
                onClick={() => onEditPosition(parties.find((p) => p.key === myParty), {
                  status: 'countered',
                  stance: point.aiSuggestion.text,
                  rationale: 'Compromise proposal (from AI suggestion, reviewed)',
                })}
              >
                Use as my counter-proposal
              </button>
              <span className="u-xs u-muted">AI suggestion — review before proposing</span>
            </div>
          )}
        </div>
      )}

      <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
        {myParty && point.status !== 'agreed' && !point.locked && (
          <button
            className="cs-btn cs-btn-outline cs-btn-sm"
            onClick={() => onEditPosition(parties.find((p) => p.key === myParty), { status: 'countered' })}
          >
            Propose compromise
          </button>
        )}
        {canApply && (
          <button className="cs-btn cs-btn-primary cs-btn-sm" disabled={!!busy} onClick={apply}>
            {busy === 'apply' ? 'Publishing…' : 'Apply agreed language to draft'}
          </button>
        )}
        {point.status === 'agreed' && !canApply && agreedStance && clause && agreedStance === clause.text && (
          <span className="u-xs u-muted">✓ Agreed language is in the current draft</span>
        )}
      </div>
    </div>
  );
}

// The Register. Default: negotiation view — points grouped by document
// section (or category), current language + a column per party with
// The Decisions log — every accept / reject / counter / status change on any
// point, newest first, read from the deal audit trail (so nothing disappears
// when a point leaves the Open slice). Click a row to open the point.
function DecisionsLog({ scope, points, onOpen }) {
  const { docs: audit } = useEntityList('audit', scope, { orderBy: 'createdAt', dir: 'desc' });
  const [range, setRange] = useState('all'); // 'week' | 'all'
  const byTitle = useMemo(() => new Map((points || []).map((p) => [p.title, p])), [points]);
  const rows = useMemo(() => {
    const since = range === 'week' ? Date.now() - 7 * 86400000 : 0;
    return (audit || []).filter((a) => DP_DECISION_AUDIT_TYPES.has(a.type) && (a.createdAt || 0) >= since);
  }, [audit, range]);
  const kindOf = (a) => {
    if (a.type === 'dealPoint.proposalAccepted' || a.type === 'dealPoint.accepted') return 'accepted';
    if (a.type === 'dealPoint.proposalRejected') return 'rejected';
    if (a.type === 'dealPoint.undo') return 'open';
    if (a.type === 'dealPoint.position') return /countered/.test(a.summary) ? 'countered' : (/agreed/.test(a.summary) ? 'accepted' : (/rejected/.test(a.summary) ? 'rejected' : 'open'));
    const m = String(a.summary || '').match(/→ (\w+)$/);
    const s = m ? m[1] : 'open';
    return s === 'agreed' ? 'accepted' : (DP_DECISION_LABELS[s] ? s : 'open');
  };
  const pointOf = (a) => {
    const m = String(a.summary || '').match(/^"(.+?)"/) || String(a.summary || '').match(/on "(.+?)"/);
    return m ? byTitle.get(m[1]) : null;
  };
  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
      <div className="u-split">
        <span className="u-small u-muted">
          {audit === null ? 'Loading…' : `${rows.length} decision${rows.length === 1 ? '' : 's'}${range === 'week' ? ' in the last 7 days' : ''}`}
        </span>
        <div className="cs-viewmode" role="group" aria-label="Decision range">
          <button type="button" className={range === 'week' ? 'active' : ''} onClick={() => setRange('week')}>This week</button>
          <button type="button" className={range === 'all' ? 'active' : ''} onClick={() => setRange('all')}>All time</button>
        </div>
      </div>
      {audit && !rows.length && <div className="cs-empty">No decisions recorded{range === 'week' ? ' this week' : ' yet'}.</div>}
      {rows.length > 0 && (
        <div className="u-scroll-x">
          <table className="cs-table">
            <thead>
              <tr><th>When</th><th>Decision</th><th>Deal point</th><th>By</th><th>Detail</th></tr>
            </thead>
            <tbody>
              {rows.map((a) => {
                const kind = kindOf(a);
                const p = pointOf(a);
                return (
                  <tr key={a.id} style={{ cursor: p ? 'pointer' : undefined }} onClick={() => p && onOpen(p)}>
                    <td className="u-tabular u-small u-muted" style={{ whiteSpace: 'nowrap' }}>{new Date(a.createdAt).toLocaleString()}</td>
                    <td><span className={`cs-badge ${DP_DECISION_TONE[kind] || ''}`}>{DP_DECISION_LABELS[kind] || kind}</span></td>
                    <td>{p ? <strong>{p.title}</strong> : <span className="u-muted">(deleted point)</span>}</td>
                    <td className="u-small">{a.byName}{a.partyKey ? <span className="u-muted"> ({a.partyKey})</span> : null}</td>
                    <td className="u-small u-muted">{a.summary}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// accept / reject / counter. Matrix view remains for fast scanning.
export default function DealPointsRegister({ scope, deal, participant }) {
  const { user } = useAuth();
  const toast = useToast();
  const { docs: points } = useEntityList('dealPoint', scope, { orderBy: 'createdAt', dir: 'asc' });
  const [view, setView] = useState('nego'); // 'nego' | 'matrix' | 'decisions'
  // Decision filter persists per deal so coming back after accepting a batch
  // shows the same slice the user left.
  const filterKey = `fl:dp-filter:${scope.deal}`;
  const [filterStatus, setFilterStatusState] = useState(() => {
    try { return localStorage.getItem(filterKey) || 'all'; } catch { return 'all'; }
  });
  const setFilterStatus = (v) => {
    setFilterStatusState(v);
    try { localStorage.setItem(filterKey, v); } catch { /* private mode */ }
  };
  // Deep links from the dashboard trackers (and Nova's navigate_app):
  // ?tab=points#decisions opens the log; #accepted / #rejected / … set the chip.
  const { hash } = useLocation();
  useEffect(() => {
    const h = (hash || '').slice(1);
    if (h === 'decisions') setView('decisions');
    else if (h === 'all' || DP_DECISIONS.includes(h)) { setView((v) => (v === 'decisions' ? 'nego' : v)); setFilterStatus(h); }
  }, [hash]); // eslint-disable-line react-hooks/exhaustive-deps
  const [filterCategory, setFilterCategory] = useState('all');
  const [editing, setEditing] = useState(null); // { point, party, preset }
  const [detail, setDetail] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showMail, setShowMail] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [memoBusy, setMemoBusy] = useState(false);

  const parties = deal.parties || [];
  const partyColors = Object.fromEntries(parties.map((p, i) => [p.key, PARTY_COLORS[i % PARTY_COLORS.length]]));
  const actor = actorOf(user, participant);

  const { live, suggestions } = useMemo(() => {
    const all = points || [];
    return {
      live: all.filter((p) => !p.suggested),
      suggestions: all.filter((p) => p.suggested),
    };
  }, [points]);

  const clauseMap = useCurrentClauses(scope, live);

  const counts = useMemo(() => {
    const c = { all: live.length };
    for (const d of DP_DECISIONS) c[d] = 0;
    for (const p of live) c[decisionOf(p)] += 1;
    return c;
  }, [live]);

  const filtered = live.filter(
    (p) =>
      (filterStatus === 'all' || decisionOf(p) === filterStatus) &&
      (filterCategory === 'all' || p.category === filterCategory)
  );

  // Group by document section (first linked clause heading), else category.
  const groups = useMemo(() => {
    const byKey = new Map();
    for (const p of filtered) {
      const ref = (p.clauseRefs || [])[0];
      const key = ref?.heading ? `§ ${ref.heading}` : (CATEGORY_LABELS[p.category] || 'Other');
      if (!byKey.has(key)) byKey.set(key, []);
      byKey.get(key).push(p);
    }
    return [...byKey.entries()];
  }, [filtered]);

  const accept = async (p) => {
    try {
      await acceptSuggestedPoint(scope, actor, p);
      toast('Added to the register', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  const dismiss = async (p) => {
    try {
      await deleteEntity('dealPoint', scope, p.id);
      toast('Suggestion dismissed', 'ok');
    } catch (err) {
      toast(err.message, 'danger');
    }
  };

  return (
    <div className="u-stack" style={{ '--gap': 'var(--sp-5)' }}>
      {suggestions.length > 0 && (
        <div className="cs-banner b-info u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <strong className="u-small">AI-suggested deal points from smart import — accept or dismiss</strong>
          {suggestions.map((p) => (
            <div key={p.id} className="u-split" style={{ gap: 'var(--sp-3)' }}>
              <span className="u-small" style={{ flex: 1 }}>
                <strong>{p.title}</strong>
                {p.detail && <span className="u-muted"> — {p.detail}</span>}
              </span>
              <span className="u-cluster" style={{ '--gap': 'var(--sp-2)', flexShrink: 0 }}>
                <button className="cs-btn cs-btn-outline cs-btn-sm" onClick={() => accept(p)}>Accept</button>
                <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => dismiss(p)}>Dismiss</button>
              </span>
            </div>
          ))}
        </div>
      )}

      <div className="u-split">
        <div className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          <div className="cs-viewmode" role="group" aria-label="Register view">
            <button type="button" className={view === 'nego' ? 'active' : ''} onClick={() => setView('nego')}>Negotiation</button>
            <button type="button" className={view === 'matrix' ? 'active' : ''} onClick={() => setView('matrix')}>Matrix</button>
            <button type="button" className={view === 'decisions' ? 'active' : ''} onClick={() => setView('decisions')}>Decisions</button>
          </div>
          {view !== 'decisions' && (
            <div className="u-cluster" role="group" aria-label="Filter by decision" style={{ '--gap': 'var(--sp-1)' }}>
              {['all', ...DP_DECISIONS].map((d) => (
                <button
                  key={d}
                  type="button"
                  className={`cs-chip${filterStatus === d ? ' active' : ''}`}
                  aria-pressed={filterStatus === d}
                  data-nova-note={`filter deal points: ${d === 'all' ? 'all' : DP_DECISION_LABELS[d]}`}
                  onClick={() => setFilterStatus(d)}
                >
                  {d === 'all' ? 'All' : DP_DECISION_LABELS[d]} <span className="u-tabular u-muted">{counts[d] || 0}</span>
                </button>
              ))}
            </div>
          )}
          {view !== 'decisions' && (
          <select className="cs-input" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} aria-label="Filter by category">
            <option value="all">All categories</option>
            {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          )}
        </div>
        <span className="u-cluster" style={{ '--gap': 'var(--sp-2)' }}>
          {isAiConfigured() && (
            <button
              className="cs-btn cs-btn-outline"
              data-tip="Paste an email thread or counterparty letter — it's reconciled against the register"
              onClick={() => setShowMail(true)}
            >
              File correspondence
            </button>
          )}
          {isAiConfigured() && live.length > 0 && (
            <button
              className="cs-btn cs-btn-outline"
              data-tip="Verify every register point is reflected in the current draft — per-point evidence"
              onClick={() => setShowVerify(true)}
            >
              Verify draft
            </button>
          )}
          {live.length > 0 && (
            <button
              className="cs-btn cs-btn-outline"
              disabled={memoBusy}
              data-tip="File an open-items memo document built from the register (deterministic — no AI in the loop)"
              onClick={async () => {
                setMemoBusy(true);
                try {
                  const r = await aiGenerateMemo({ orgId: scope.org, dealId: scope.deal, kind: 'open-items' });
                  toast(`Filed "${r.title}" — see the Documents tab`, 'ok');
                } catch (err) {
                  toast(err.message, 'danger');
                } finally {
                  setMemoBusy(false);
                }
              }}
            >
              {memoBusy ? 'Filing…' : 'Open-items memo'}
            </button>
          )}
          {live.length > 0 && (
            <button
              className="cs-btn cs-btn-outline"
              disabled={exporting}
              data-tip={view === 'decisions'
                ? 'The decisions log as a PDF — every accept / reject / counter with who, when and the resulting status'
                : 'The register as a styled PDF — status chips, every party\'s stance per point, landing-zone notes in italics'}
              onClick={async () => {
                setExporting(true);
                try {
                  await downloadWordExport({
                    orgId: scope.org,
                    dealId: scope.deal,
                    docId: view === 'decisions' ? '@dealPoints:decisions' : '@dealPoints',
                    mode: 'clean',
                    format: 'pdf',
                    oursPartyKey: participant?.partyKey || '',
                  });
                } catch (err) {
                  toast(err.message, 'danger');
                } finally {
                  setExporting(false);
                }
              }}
            >
              {exporting ? 'Exporting…' : 'Export PDF'}
            </button>
          )}
          <button className="cs-btn cs-btn-primary" onClick={() => setShowNew(true)}>New deal point</button>
        </span>
      </div>

      {showMail && <CorrespondenceModal scope={scope} onClose={() => setShowMail(false)} />}
      {showVerify && <ConformanceModal scope={scope} onClose={() => setShowVerify(false)} />}

      {!participant && (
        <div className="cs-banner b-warn u-small">
          You're not on this deal's team with a party affiliation yet — you can record positions for any party,
          but Accept / Reject / Propose compromise act on behalf of <em>your</em> party. Add yourself under Team.
        </div>
      )}

      {points === null && <div className="cs-skeleton" style={{ height: 200 }} />}
      {view !== 'decisions' && points && !filtered.length && (
        <div className="cs-empty">
          No deal points{filterStatus !== 'all' || filterCategory !== 'all' ? ' match the filters' : ' yet — add one, or import a document and let AI suggest them'}.
        </div>
      )}

      {view === 'decisions' && (
        <DecisionsLog scope={scope} points={live} onOpen={(p) => setDetail(p)} />
      )}

      {view === 'nego' && groups.map(([groupLabel, groupPoints]) => (
        <div key={groupLabel} className="u-stack" style={{ '--gap': 'var(--sp-3)' }}>
          <div className="cs-nego-group-head">{groupLabel}</div>
          {groupPoints.map((point) => (
            <NegotiationCard
              key={point.id}
              scope={scope}
              dealId={scope.deal}
              point={point}
              parties={parties}
              partyColors={partyColors}
              participant={participant}
              clauseMap={clauseMap}
              dealPoints={points}
              onDetail={() => setDetail(point)}
              onEditPosition={(party, preset) => setEditing({ point, party, preset })}
            />
          ))}
        </div>
      ))}

      {view === 'matrix' && filtered.length > 0 && (
        <div className="u-scroll-x">
          <table className="cs-table">
            <thead>
              <tr>
                <th>Deal point</th>
                <th>Status</th>
                {parties.map((p) => <th key={p.key}>{p.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {filtered.map((point) => (
                <tr key={point.id}>
                  <td style={{ maxWidth: 320 }}>
                    <button
                      type="button"
                      className="cs-btn cs-btn-ghost cs-btn-sm"
                      style={{ textAlign: 'left', whiteSpace: 'normal' }}
                      onClick={() => setDetail(point)}
                    >
                      <span aria-hidden="true" className="u-muted" data-tip={`priority: ${point.priority}`}>{PRIORITY_MARK[point.priority] || ''}</span>{' '}
                      {point.title}
                      {point.locked && <span className="cs-badge b-warn" style={{ marginLeft: 'var(--sp-2)' }}>locked</span>}
                      {point.needsReview && (
                        <span className="cs-badge b-danger" style={{ marginLeft: 'var(--sp-2)' }} data-tip={point.needsReviewNote || 'A received draft changed the linked language'}>
                          needs review
                        </span>
                      )}
                      {point.visibility && point.visibility !== 'internal' && (
                        <span className={`cs-badge ${VISIBILITY_TONE[point.visibility] || ''}`} style={{ marginLeft: 'var(--sp-2)' }}>
                          {VISIBILITY_LABELS[point.visibility] || point.visibility}
                        </span>
                      )}
                    </button>
                  </td>
                  <td>
                    <span className={`cs-badge ${DP_DECISION_TONE[decisionOf(point)] || ''}`}>{DP_DECISION_LABELS[decisionOf(point)]}</span>
                  </td>
                  {parties.map((party) => {
                    const pos = (point.positions || {})[party.key];
                    return (
                      <td key={party.key}>
                        <button
                          type="button"
                          className="cs-btn cs-btn-ghost cs-btn-sm u-stack"
                          style={{ '--gap': '2px', alignItems: 'flex-start', textAlign: 'left', maxWidth: 220 }}
                          onClick={() => setEditing({ point, party })}
                          title={pos?.rationale || ''}
                        >
                          {pos ? (
                            <>
                              <span className={`cs-badge ${POS_TONE[pos.status] || ''}`}>{POSITION_STATUS_LABELS[pos.status] || pos.status}</span>
                              {pos.stance && <span className="u-small u-clamp" style={{ '--lines': 2 }}>{pos.stance}</span>}
                            </>
                          ) : (
                            <span className="u-small u-muted">+ position</span>
                          )}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <PositionModal
          scope={scope}
          participant={participant}
          point={(points || []).find((p) => p.id === editing.point.id) || editing.point}
          party={editing.party}
          preset={editing.preset}
          onClose={() => setEditing(null)}
        />
      )}
      {detail && (
        <PointDetailModal
          scope={scope}
          participant={participant}
          point={(points || []).find((p) => p.id === detail.id) || detail}
          parties={parties}
          onClose={() => setDetail(null)}
        />
      )}
      {showNew && <NewPointModal scope={scope} participant={participant} onClose={() => setShowNew(false)} />}
    </div>
  );
}
